import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUserContext } from '../contexts/UserContext';
import { useWorkspace } from '../workspace/state/workspaceStore';
import { appPath } from '../utils/appRouting';

const ProtectedRoute = ({ children, allowIncompleteProfile = false }) => {
  const { currentUser, emailVerified, loading, backendVerifying, userProfile } = useAuth();
  const location = useLocation();
  const userCtx = useUserContext();
  const ws = useWorkspace();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to={appPath('/login')} />;
  }

  // Se email non verificata, redirect alla pagina di verifica
  if (!emailVerified) {
    return <Navigate to={appPath('/verify-email')} />;
  }

  // Aspetta il profilo backend quando necessario
  if (!allowIncompleteProfile && backendVerifying && !userProfile) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifica profilo...</p>
        </div>
      </div>
    );
  }

  const profileComplete =
    userProfile?.profileComplete ??
    userProfile?.profile_complete ??
    true;

  if (!allowIncompleteProfile && profileComplete === false) {
    return <Navigate to={appPath('/complete-profile')} replace />;
  }

  // Gate onboarding: richiede almeno 1 classe (curricolare) o 1 alunno (sostegno)
  // prima di consentire accesso alle pagine che creano PEI / Verbale / PDP.
  // Tutte le altre pagine devono rimanere accessibili anche senza alunni/classi.
  const rawPath = String(location.pathname || '');
  const path = rawPath.startsWith('/app/') ? rawPath.slice(4) : rawPath;

  // Applica il gate SOLO sulle pagine che richiedono uno studente/classe già creato.
  const requiresOnboarding =
    // PDP wizard (creazione/compilazione)
    path.startsWith('/pdp/') ||
    // Verbale GLO (sempre legato ad uno studente)
    (path.startsWith('/students/') && path.includes('/verbale-glo')) ||
    // PEI ministeriale (sempre legato ad uno studente)
    path.startsWith('/pei-ministeriale/') ||
    // Verifica PEI (richiede studentId)
    path.startsWith('/pei/verifica');

  if (requiresOnboarding) {
    // Aspetta il primo fetch di /users/context (contiene usageCounters)
    // MA se lo store ha già dati, non bloccare.
    const storeStudents = Array.isArray(ws?.students) ? ws.students.length : 0;
    const storeClasses = Array.isArray(ws?.classes) ? ws.classes.length : 0;
    const storeHasSomething = storeStudents > 0 || storeClasses > 0;

    if (!storeHasSomething && (userCtx?.loading || userCtx?.hasLoadedOnce === false)) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto"></div>
            <p className="mt-4 text-gray-600">Preparazione account…</p>
          </div>
        </div>
      );
    }

    const teachingRoleRaw = String(userProfile?.teaching_role || userProfile?.teachingRole || '').trim().toLowerCase();
    const roleTypeRaw = String(userCtx?.roleType || userProfile?.ruolo || userProfile?.role || '').trim().toLowerCase();

    const isTeacherProfile =
      teachingRoleRaw === 'curricolare' ||
      teachingRoleRaw === 'sostegno' ||
      roleTypeRaw.includes('docente');

    const teachingRole =
      teachingRoleRaw === 'curricolare' || teachingRoleRaw === 'sostegno'
        ? teachingRoleRaw
        : roleTypeRaw.includes('curricolare')
          ? 'curricolare'
          : 'sostegno';

    const countersStudents = Number(userCtx?.usageCounters?.studentsCount ?? 0);
    const countersClasses = Number(userCtx?.usageCounters?.classesCount ?? 0);

    // Fallback: se i counters backend sono temporaneamente errati/stale,
    // usa anche i dati già caricati nello store workspace.
    const storeStudents2 = Array.isArray(ws?.students) ? ws.students.length : 0;
    const storeClasses2 = Array.isArray(ws?.classes) ? ws.classes.length : 0;

    const studentsCount = Math.max(countersStudents, storeStudents2);
    const classesCount = Math.max(countersClasses, storeClasses2);

    const onboardingMissing = isTeacherProfile && (teachingRole === 'curricolare' ? classesCount <= 0 : studentsCount <= 0);

    if (onboardingMissing) {
      const preferred = teachingRole === 'curricolare' ? 'class' : 'student';
      const base = appPath('/my-context');
      const target = `${base}?onboarding=1&preferred=${encodeURIComponent(preferred)}`;
      return <Navigate to={target} replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
